import { LocalStorage } from 'libs/storage/LocalStorage';
function normalize(value) {
    const trimmed = value === null || value === void 0 ? void 0 : value.trim().toLowerCase();
    return trimmed ? trimmed : undefined;
}
export const CONSENT_VERSION = 1;
function storageKey(host) {
    return `cookie-consent.${host}`;
}
/**
 * Restores the visitor's stored cookie-consent choice for this host, if any.
 * Returns null when absent, malformed, or from an older consent version.
 */
export function loadConsentChoice(host) {
    try {
        const stored = LocalStorage.get(storageKey(host));
        if (stored == null || typeof stored !== 'object') {
            return null;
        }
        if (typeof stored.analytics !== 'boolean' ||
            typeof stored.ads !== 'boolean' ||
            typeof stored.version !== 'number' ||
            typeof stored.decidedAt !== 'number') {
            return null;
        }
        if (stored.version !== CONSENT_VERSION) {
            return null;
        }
        return {
            analytics: stored.analytics,
            ads: stored.ads,
            version: stored.version,
            decidedAt: stored.decidedAt
        };
    }
    catch (_a) {
        return null;
    }
}
export function saveConsentChoice(host, choice) {
    LocalStorage.set(storageKey(host), {
        analytics: choice.analytics,
        ads: choice.ads,
        version: CONSENT_VERSION,
        decidedAt: Date.now()
    });
}
/**
 * Ensures `window.gtag` exists and pushes via the Arguments object, which
 * GTM recognizes as a Consent Mode command. Pushing a plain Array is
 * unpacked by GTM and never fires a consent update.
 */
function ensureGtag() {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
        window.gtag = function gtag() {
            // GTM only treats Arguments objects as consent commands.
            // eslint-disable-next-line prefer-rest-params
            window.dataLayer.push(arguments);
        };
    }
    return window.gtag;
}
/**
 * Sets Google Consent Mode v2 to denied for every category, before GTM
 * loads. Must run before pushConsentUpdate/injectGtmScript.
 */
export function pushConsentDefault() {
    ensureGtag()('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        wait_for_update: 500
    });
}
export function pushConsentUpdate(choice) {
    ensureGtag()('consent', 'update', {
        analytics_storage: choice.analytics ? 'granted' : 'denied',
        ad_storage: choice.ads ? 'granted' : 'denied',
        ad_user_data: choice.ads ? 'granted' : 'denied',
        ad_personalization: choice.ads ? 'granted' : 'denied'
    });
}
/**
 * Custom event so GTM can trigger tags after a visitor chooses on the
 * banner. Not used when restoring a stored choice on page load.
 */
export function pushConsentUpdateEvent() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'consent_update' });
}
/**
 * Pushes normalized (not hashed) email/phone into a `user_data` dataLayer
 * variable, for GTM's built-in Enhanced Conversions / Advanced Matching
 * tags to pick up and hash themselves. Must be called before the
 * conversion event it applies to. No-op unless the visitor granted ads
 * consent, and unless at least one identifier is available.
 */
export function pushEnhancedConversionUserData(host, identifiers) {
    var _a;
    if (!((_a = loadConsentChoice(host)) === null || _a === void 0 ? void 0 : _a.ads)) {
        return;
    }
    const email = normalize(identifiers.email);
    const phoneNumber = normalize(identifiers.phone);
    if (!email && !phoneNumber) {
        return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        user_data: Object.assign(Object.assign({}, (email ? { email } : {})), (phoneNumber ? { phone_number: phoneNumber } : {}))
    });
}
/**
 * Injects the GTM container script once. Safe to call on every render;
 * guards against double-injection for the same container id.
 */
export function injectGtmScript(gtmId) {
    var _a;
    if ((_a = window
        .google_tag_manager) === null || _a === void 0 ? void 0 : _a[gtmId])
        return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
    });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(script);
}
